import React, { useState } from 'react';
import PropTypes from 'prop-types';
import intl from 'react-intl-universal';
import { Button, Box, Typography, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import api from 'utils/api';
import FormField from 'components/FormField';
import { OrganizationProptypes } from 'shared/propTypes/organizationType';
import { DOLPHIN_API } from 'shared/constants/apis';
import pxToRem from 'assets/theme/functions/pxToRem';
import { ConfirmModal } from 'shared/modals/confirmModal';
import logger from 'utils/logger';
import { useSnackbar } from 'notistack';
const propTypes = {
  organizationInfo: PropTypes.shape(OrganizationProptypes),
};

// Create a logger instance for this component
const log = logger.createLogger('OrganizationProfileTab');

const ProfileTab = ({ organizationInfo }) => {
  const snackbar = useSnackbar();
  const [showConfirmBeforeSend, setShowConfirmBeforeSend] = useState(false);

  const [formState, setFormState] = useState({
    contactName: organizationInfo.contact.name,
    zipCode: organizationInfo.contact.zipCode,
    addressLine1: organizationInfo.contact.addressLine1,
    addressLine2: organizationInfo.contact.addressLine2,
    tel: organizationInfo.contact.tel,
    fax: organizationInfo.contact.fax,
    bank: organizationInfo.paymentMethod.bank,
    branch: organizationInfo.paymentMethod.branch,
    accountType: organizationInfo.paymentMethod.accountType,
    accountNo: organizationInfo.paymentMethod.accountNo,
    accountName: organizationInfo.paymentMethod.accountName,
  });

  const handleChange = (key) => async (event) => {
    const newFormState = { ...formState, [key]: event.target.value };
    setFormState({ ...newFormState });
  };

  const handleUpdateClick = async () => {
    setShowConfirmBeforeSend(true);
  };

  const handleConfirmClick = async () => {
    setShowConfirmBeforeSend(false);
    await sendToServer();
  };

  const wasChanged = (formerValue, currentValue) => {
    if (
      formerValue !== undefined &&
      currentValue !== undefined &&
      formerValue.toString() === currentValue.toString()
    ) {
      return false;
    } else if (formerValue === undefined && currentValue === '') {
      return false;
    } else if (formerValue === undefined && currentValue === undefined) {
      return false;
    }
    return true;
  };

  const createConfirmWindowContents = (formerValue, currentValue, title) => {
    if (wasChanged(formerValue, currentValue)) {
      let formerValue_ = intl.get('shared_value_none');
      if (formerValue !== undefined && formerValue !== '') {
        formerValue_ = formerValue;
      }

      let currentValue_ = intl.get('shared_value_none');
      if (currentValue !== undefined && currentValue !== '') {
        currentValue_ = currentValue;
      }

      return (
        <Grid container>
          <Grid size={4}>
            <Typography variant="body1">
              <strong>{intl.get(title)}</strong>
            </Typography>
          </Grid>
          <Grid>
            <Typography variant="body1">
              {formerValue_} → {currentValue_}
            </Typography>
          </Grid>
        </Grid>
      );
    }
  };

  const generateConfirmWindowContents = () => {
    return (
      <Stack spacing={3} sx={{ paddingTop: '20px', paddingBottom: '20px' }}>
        {createConfirmWindowContents(
          organizationInfo.contact.name,
          formState.contactName,
          'organization_settings_preference_label_contact_name'
        )}
        {createConfirmWindowContents(
          organizationInfo.contact.zipCode,
          formState.zipCode,
          'organization_settings_preference_label_zip_code'
        )}
        {createConfirmWindowContents(
          organizationInfo.contact.addressLine1,
          formState.addressLine1,
          'organization_settings_preference_label_address_line_1'
        )}
        {createConfirmWindowContents(
          organizationInfo.contact.addressLine2,
          formState.addressLine2,
          'organization_settings_preference_label_address_line_2'
        )}
        {createConfirmWindowContents(
          organizationInfo.contact.tel,
          formState.tel,
          'organization_settings_preference_label_tel'
        )}
        {createConfirmWindowContents(
          organizationInfo.contact.fax,
          formState.fax,
          'organization_settings_preference_label_fax'
        )}
        {createConfirmWindowContents(
          organizationInfo.paymentMethod.bank,
          formState.bank,
          'organization_settings_preference_label_bank'
        )}
        {createConfirmWindowContents(
          organizationInfo.paymentMethod.branch,
          formState.branch,
          'organization_settings_preference_label_branch'
        )}
        {createConfirmWindowContents(
          organizationInfo.paymentMethod.accountType,
          formState.accountType,
          'organization_settings_preference_label_account_type'
        )}
        {createConfirmWindowContents(
          organizationInfo.paymentMethod.accountNo,
          formState.accountNo,
          'organization_settings_preference_label_account_no'
        )}
        {createConfirmWindowContents(
          organizationInfo.paymentMethod.accountName,
          formState.accountName,
          'organization_settings_preference_label_accountName'
        )}
      </Stack>
    );
  };

  const sendToServer = async () => {
    const variables = {
      params: {
        organizationId: organizationInfo.id,
      },
      data: { ...formState },
    };

    api.post(DOLPHIN_API.ORGANIZATION_INFO, variables).then(
      () => {
        snackbar.enqueueSnackbar(intl.get('shared_data_was_updated'), {
          variant: 'success',
        });
        window.location.reload();
      },
      (error) => {
        log.error('Error in organization info', { error });
      }
    );
  };

  return (
    <>
      <Stack spacing={3}>
        <Stack spacing={3}>
          <Box mb={3}>
            <Typography variant="h5">
              {intl.get('organization_settings_preference_text_general')}
            </Typography>
          </Box>
          <FormField
            id="organizationCode"
            name="organizationCode"
            label={intl.get(
              'organization_settings_preference_label_organization_code'
            )}
            variant="outlined"
            value={organizationInfo.organizationCode}
          />
          <FormField
            id="organizationName"
            name="organizationName"
            label={intl.get(
              'organization_settings_preference_label_organization_name'
            )}
            variant="outlined"
            value={organizationInfo.name}
            inputProps={{ maxLength: 17 }}
          />
        </Stack>

        <Stack spacing={3} sx={{ mt: 2 }}>
          <Box mb={2}>
            <Typography variant="h5" gutterBottom>
              {intl.get('organization_settings_preference_text_contact')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid size={6}>
              <FormField
                id="contactName"
                name="contactName"
                label={intl.get(
                  'organization_settings_preference_label_contact_name'
                )}
                fullWidth
                multiline
                variant="outlined"
                color="secondary"
                value={formState.contactName}
                onChange={handleChange('contactName')}
              />
            </Grid>
            <Grid size={6}>
              <FormField
                id="zipCode"
                name="zipCode"
                label={intl.get(
                  'organization_settings_preference_label_zip_code'
                )}
                fullWidth
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.zipCode}
                onChange={handleChange('zipCode')}
              />
            </Grid>
            <Grid size={6}>
              <FormField
                id="addressLine1"
                name="addressLine1"
                label={intl.get(
                  'organization_settings_preference_label_address_line_1'
                )}
                fullWidth
                multiline
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.addressLine1}
                onChange={handleChange('addressLine1')}
              />
            </Grid>
            <Grid size={6}>
              <FormField
                id="addressLine2"
                name="addressLine2"
                label={intl.get(
                  'organization_settings_preference_label_address_line_2'
                )}
                fullWidth
                multiline
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.addressLine2}
                onChange={handleChange('addressLine2')}
              />
            </Grid>
            <Grid size={6}>
              <FormField
                id="tel"
                name="tel"
                label={intl.get('organization_settings_preference_label_tel')}
                fullWidth
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.tel}
                onChange={handleChange('tel')}
              />
            </Grid>
            <Grid size={6}>
              <FormField
                id="fax"
                name="fax"
                label={intl.get('organization_settings_preference_label_fax')}
                fullWidth
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.fax}
                onChange={handleChange('fax')}
              />
            </Grid>
          </Grid>
        </Stack>

        <Stack spacing={3} sx={{ mt: 2 }}>
          <Box mb={2}>
            <Typography variant="h5" gutterBottom>
              {intl.get(
                'organization_settings_preference_text_receiving_account'
              )}
            </Typography>
          </Box>
          <Grid container spacing={3}>
            <Grid size={6}>
              <FormField
                id="bank"
                name="bank"
                label={intl.get('organization_settings_preference_label_bank')}
                fullWidth
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.bank}
                onChange={handleChange('bank')}
              />
            </Grid>
            <Grid size={6}>
              <FormField
                id="branch"
                name="branch"
                label={intl.get(
                  'organization_settings_preference_label_branch'
                )}
                fullWidth
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.branch}
                onChange={handleChange('branch')}
              />
            </Grid>
            <Grid size={6}>
              <FormField
                id="accountType"
                name="accountType"
                label={intl.get(
                  'organization_settings_preference_label_account_type'
                )}
                fullWidth
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.accountType}
                onChange={handleChange('accountType')}
              />
            </Grid>
            <Grid size={6}>
              <FormField
                id="accountNo"
                name="accountNo"
                label={intl.get(
                  'organization_settings_preference_label_account_no'
                )}
                fullWidth
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.accountNo}
                onChange={handleChange('accountNo')}
              />
            </Grid>
            <Grid size={12}>
              <FormField
                id="accountName"
                name="accountName"
                label={intl.get(
                  'organization_settings_preference_label_account_name'
                )}
                fullWidth
                multiline
                autoComplete="no name"
                variant="outlined"
                color="secondary"
                value={formState.accountName}
                onChange={handleChange('accountName')}
              />
            </Grid>
          </Grid>
        </Stack>

        <Button
          variant="contained"
          color="primary"
          style={{ color: 'white', fontSize: pxToRem(18) }}
          fullWidth
          onClick={handleUpdateClick}>
          {intl.get('organization_settings_preference_button_update')}
        </Button>
      </Stack>

      <ConfirmModal
        isOpened={showConfirmBeforeSend}
        onCancel={() => setShowConfirmBeforeSend(false)}
        onConfirm={handleConfirmClick}
        title={intl.get(
          'organization_settings_preference_modal_text_confirm_title'
        )}
        bodyText={intl.get(
          'organization_settings_preference_modal_text_confirm_hint'
        )}
        confirmButtonText={intl.get(
          'organization_settings_preference_modal_button_submit'
        )}
        cancelButtonText={intl.get(
          'organization_settings_preference_modal_button_cancel'
        )}
        confirmButtonColor="info"
        cancelButtonColor="secondary">
        <div style={{ width: '700px' }}>
          {showConfirmBeforeSend && generateConfirmWindowContents()}
        </div>
      </ConfirmModal>
    </>
  );
};

ProfileTab.propTypes = propTypes;

export default ProfileTab;
